import { useContext, useState, useRef, useEffect } from "react";
import { Box, FormControl, styled, Autocomplete, TextField, CircularProgress } from "@mui/material";
import SiteForm from "./SiteForm"; 
import LocationManagement from "./LocationManagement";
import Site from "../../../../api/Admin/Site";
import ModalSimpleButton from '../../../../components/Modal/ModalSimpleButton';
import { GlobalSecondaryButton, GlobalButton } from "../../../styles";
import { LocationContext } from "../../../../context/LocationContext";
import UserHomeSiteManagement from "./UserHomeSiteManagement";
import CustomModal from "../../../../components/Modal";
import { useAuth } from "../../../../context/AuthContext";
import { Roles } from "../../../../constants/roles";  

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  marginBottom: "20px"
}));

const StyledFormControl = styled(FormControl)(() => ({
  display: 'flex',
  width: "50rem"
}));

const initialState = {
  siteName: "",
  abbreviation: "",
  singleLetterAbv: "",
  isActive: false,
  isExternalSite: false,
  siteContact: "",
  address: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  phoneNumber: "",
  locations: []
};

const SiteLocationManagement = () => {
  const [isNewSite, setIsNewSite] = useState(false);
  const [siteData, setSiteData] = useState(initialState);
  const [siteList, setSiteList] = useState([]);
  const [dataIsLoading, setDataIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showManageGuestUsersModal, setShowManageGuestUsersModal] = useState(false);

  const [modalSimpleButton, setModalSimpleButton] = useState(false);
  const [modalSimpleButtonText, setModalSimpleButtonText] = useState('');
  const [modalSimpleTitle, setModalSimpleTitle] = useState('');
  const [modalSimpleText, setModalSimpleText] = useState('');

  const siteFormikRef = useRef();

  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole([Roles.SiteSME, Roles.Developer]);

  const {locations, getAllLocations } = useContext(LocationContext);
  const [allLocationsLoading, setAllLocationsLoading] = useState(false);

  const resetData = () => setSiteData(initialState);  

  useEffect(() => {
    loadAllSites();
    loadAllLocations();
  // eslint-disable-next-line
  }, []);

  function loadAllSites() {
    Site.getAll().then((res) => {
      if (res.length > 0) {

        let array = [];
        array.push({ siteName: "", abbreviation: "" });
        array.push(...res);

        setSiteList(array.sort(
          (a,b) => a.siteName.localeCompare(b.siteName)
        ));
      }
    }); 
  }

  const loadAllLocations = async(reload=false) => {
    if (!canEdit) return;
    setAllLocationsLoading(true);

    if (reload || (!locations || locations.length === 0)) {
      await getAllLocations();
    }

    setAllLocationsLoading(false);
  }

  function handleSiteSelection(siteName) {
    resetData();
    
    if (siteName) {
      setIsNewSite(false);
      setDataIsLoading(true);

      Site.get(siteName).then((res) => {
        setDataIsLoading(false);        
        if (res) {
          setSiteData(() => ({
            siteName: res.siteName,
            abbreviation: res.abbreviation,
            singleLetterAbv: res.singleLetterAbv,
            isActive: res.isActive,
            isExternalSite: res.isExternalSite,
            siteContact: res.siteContact,
            address: res.address,
            city: res.city,
            state: res.state,
            postalCode: res.postalCode,
            country: res.country,
            phoneNumber: res.phoneNumber,
            createdByEmail: res.createdByEmail,
            createdDate: res.createdDate,
            lastModifiedByEmail: res.lastModifiedByEmail,
            lastModifiedDate: res.lastModifiedDate,
            locations: res.locations.map(loc =>({
              ...loc, 
              isModified : false,
              isNew: false
            })).sort((a,b) => a.locationName.localeCompare(b.locationName))
          }));
        }
      });
    }
  }

  function handleAddNewSite() {
    setIsNewSite(true);
    resetData();
  }

  const validateSiteFormik = async() => {
    if (!siteFormikRef.current){ 
      return false;
    }
    const errors = await siteFormikRef.current.validateForm();
    const touchedFields = Object.keys(errors).reduce((acc, key) => {
      acc[key] = true;
      return acc;
      }, {}
    );
    siteFormikRef.current.setTouched(touchedFields);
    return (Object.keys(errors).length === 0);
  }

  const handleSubmit = async(event) => {
    event.preventDefault();

    if (! await validateSiteFormik()) {
      return;
    }
    setIsSaving(true);
    const values = siteFormikRef.current.values;

    const updatedSiteData = new Site({
      siteName : values.siteName,
      abbreviation : values.abbreviation,
      singleLetterAbv: values.singleLetterAbv,
      isActive : values.isActive,
      isExternalSite : values.isExternalSite,
      siteContact : values.siteContact,
      address : values.address,
      city : values.city,
      state : values.state,
      postalCode : values.postalCode,
      country : values.country,
      phoneNumber : values.phoneNumber,
      locations : siteData.locations?.filter(loc => loc.isModified || loc.isNew) ?? []
    });

    try {
      const apiAction = isNewSite
        ? Site.add(updatedSiteData)
        : Site.update(siteData.siteName, updatedSiteData);

      await apiAction;

      resetData();
      openModalSimpleButton('Success', 'Site saved successfully!', 'Ok');
      setIsNewSite(false);
      loadAllSites();

      if (updatedSiteData.locations.length > 0){
        loadAllLocations(true);
      }

    } catch (error) {
      openModalSimpleButton('Fail', `Error saving Site : ${error?.message}`, 'Ok');      
    } finally {
      setIsSaving(false);
    }
  }

  function closeModalSimpleButton() {
    setModalSimpleButton(false);
  }

  function handleDeactivateSiteLocations(value)
  {
    const activeLocations = siteData?.locations?.filter(x => x.isActive) ?? [];
    if (value === false && activeLocations.length > 0)
    {
      setSiteData(prev => ({
        ...prev,
        locations: prev.locations.map(loc =>
          loc.isActive
            ? { ...loc, isActive: false, isModified: true }
            : loc
          )
      }));

      openModalSimpleButton('Active locations','All active site locations have been set to inactive.', 'Ok');
    }
  }

  function openModalSimpleButton(title, text, buttonText) {
    setModalSimpleButtonText(buttonText)
    setModalSimpleButton(true);
    setModalSimpleTitle(title);
    setModalSimpleText(text);
  }

  return (
    <StyledBox flexDirection="column" justifyContent="space-between">

      <StyledBox>
        <StyledFormControl sx={{ m: 1, width: "44rem" }} variant='outlined'>
          <StyledBox sx={{ justifyContent: "space-between" }}>
            <Autocomplete
              noOptionsText={"Loading Sites..."}
              value={siteData.siteName}
              onChange={(e, value) => { handleSiteSelection(value) }}
              disablePortal
              options={siteList ? siteList.map((s) => s.siteName) : []}
              sx={{ width: "35rem" }}
              renderInput={(params) => <TextField {...params} variant="outlined" size="small" margin="none" label="Site" />}
              autoSelect
            />

            { canEdit && <GlobalSecondaryButton sx={{width:"8rem", marginLeft:"1rem" }}
                variant='contained'
                type="button"
                onClick={handleAddNewSite}
              >Add New</GlobalSecondaryButton>
            }

            { canEdit && siteData?.isExternalSite === true && <GlobalSecondaryButton 
                sx={{width:"16rem", marginLeft:"1rem" }}
                variant='contained'
                type="button"
                onClick={() => setShowManageGuestUsersModal(true)}
              >Manage Guest Users</GlobalSecondaryButton>
            }

          </StyledBox> 
        </StyledFormControl>
      </StyledBox>

      {dataIsLoading && <CircularProgress 
        style={{textTransform: "none", marginLeft:"15px"}}>
      </CircularProgress>}      

      {(siteData.siteName || isNewSite) && <SiteForm
          siteData={siteData} 
          setSiteData={setSiteData}
          formikRef={siteFormikRef}
          invalidSiteNames={siteList.filter(s => s.siteName !== siteData.siteName).map(s => s.siteName)}
          invalidSiteAbbreviations={siteList.filter(s => s.abbreviation !== siteData.abbreviation).map(s => s.abbreviation)}
          invalidSingleAbvs={siteList.filter(s => s.abbreviation !== siteData.abbreviation).map(s => s.singleLetterAbv)}
          isAddSite={isNewSite}
          canEdit={canEdit}
          onDeactivateSite={handleDeactivateSiteLocations}>
        </SiteForm>
      } 

      {siteData.siteName && <LocationManagement
          siteData={siteData}
          setSiteData={setSiteData}
          canEdit={canEdit}
          allLocationsLoading={allLocationsLoading}
        />
      }

      <form onSubmit={handleSubmit}>
        { canEdit && (siteData.siteName || isNewSite) &&
          <StyledBox sx={{ mb: 1, mt: 2, justifyContent: "space-between" }}>
            <GlobalButton sx={{ width: "8rem" }}
              variant='contained'             
              type="submit"
              disabled={isSaving}
            >Save</GlobalButton>
          </StyledBox>
        }
      </form >

      <ModalSimpleButton 
        title={modalSimpleTitle} 
        buttonText={modalSimpleButtonText} 
        buttonAction={closeModalSimpleButton} 
        open={modalSimpleButton} 
        setOpen={setModalSimpleButton}
      >
        <label>
          {modalSimpleText}
        </label>
      </ModalSimpleButton>

      <CustomModal
        title={`Guest Users of site: ${siteData?.siteName}`}
        open={showManageGuestUsersModal}
        setOpen={setShowManageGuestUsersModal}
        setClose={undefined}>
        <UserHomeSiteManagement
          siteName={siteData?.siteName}  
          onClose={() => setShowManageGuestUsersModal(false)}        
        />
      </CustomModal> 

    </StyledBox>
  );
};

export default SiteLocationManagement;
